import {FC, MouseEvent, useContext, useState} from 'react';
import {PhotoProvider} from 'react-photo-view';

import {CircularProgress, IconButton, Stack, Tooltip} from '@mui/material';
import {WorkDataMedia} from './WorkDataMedia';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import WebIcon from '@mui/icons-material/Web';

import {
  InputTypeEnum,
  IWorkContentDocumentsItem,
  IWorkContentFull,
  WorkContentType,
} from '../../../../shared/types/workContent.types';
import {EventBusContext} from '../../../../providers/EventBus';
import {OverlayRenderProps} from 'react-photo-view/dist/types';
import {useRecoilValue} from 'recoil';
import {currentServiceRequestState, serviceRequestActiveTabState} from '../../../../store/service-requests/atoms';
import {IServiceRequest} from '../../../../shared/types/serviceRequest.types';
import {WorkDataFiles} from './WorkDataFiles';
import {getModelNamePrefix} from './utils';

import styles from './styles.module.scss';
import {WorkDataAudio} from './WorkDataAudio';
import {WorkDataMultiple} from './WorkDataMultiple';

interface IWorkDataDocuments {
  item: {
    type: WorkContentType;
    fields: IWorkContentFull[];
  };
  isEditable: boolean;
}

const ToolBar: FC<OverlayRenderProps & {photoNames: string[]}> = ({onScale, scale, images, index, photoNames}) => {
  const serviceRequest = useRecoilValue(currentServiceRequestState) as IServiceRequest;
  const activeTab = useRecoilValue(serviceRequestActiveTabState);
  const [loadingState, setLoadingState] = useState<Record<number, boolean>>({});
  const {bus} = useContext(EventBusContext);

  const url = images[index].src || images[index].originRef?.current?.getAttribute('data-url');

  const isVideo = !images[index].src;

  const onDownLoad = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      setLoadingState((prev) => ({...prev, [index]: true}));
      e.preventDefault();

      const anchor = document.createElement('a');
      anchor.href = url as string;
      anchor.download = `Заявка_№${serviceRequest.id}_${photoNames[index]}`;
      anchor.style.display = 'none';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch (err) {
      bus?.emit('error', [err]);
    } finally {
      setLoadingState((prev) => ({...prev, [index]: false}));
    }
  };

  const openInNewTab = (url: string) => {
    window.open(url);
  };

  return (
    <>
      {!activeTab?.editMode ? (
        <>
          <Tooltip PopperProps={{disablePortal: true}} title={`Открыть ${isVideo ? 'видео' : 'фото'} в новой вкладке`}>
            <IconButton size="small" onClick={() => openInNewTab(url as string)}>
              {loadingState[index] ? <CircularProgress sx={{height: 24, width: 24}} /> : <WebIcon htmlColor="#fff" />}
            </IconButton>
          </Tooltip>
          <IconButton disabled={loadingState[index]} onClick={onDownLoad}>
            {loadingState[index] ? (
              <CircularProgress sx={{height: 24, width: 24}} />
            ) : (
              <VerticalAlignBottomIcon htmlColor="#fff" />
            )}
          </IconButton>
        </>
      ) : null}
      {images[index].src && (
        <>
          <IconButton onClick={() => onScale(scale + 1)}>
            <ZoomInIcon htmlColor="#fff" />
          </IconButton>
          <IconButton onClick={() => onScale(scale - 1)}>
            <ZoomOutIcon htmlColor="#fff" />
          </IconButton>
        </>
      )}
    </>
  );
};

const RenderDocuments: FC<{item: IWorkContentFull; isEditable: boolean}> = ({item, isEditable}) => {
  const prefix = getModelNamePrefix(item.inputType);

  const inputType = item.inputType;

  if (inputType === InputTypeEnum.FILE_PICKER) {
    return (
      <WorkDataFiles
        item={{
          text: item.name,
          files: (item.item as IWorkContentDocumentsItem)?.documents,
          formName: `${prefix}${item.id}`,
          mandatory: item.mandatory,
          maxFilesAllowed: item.maxFilesAllowed,
          availableFileExtensions: item?.availableFileExtensions,
        }}
        isEditable={isEditable}
        key={item.id}
      />
    );
  }

  if (inputType === InputTypeEnum.IMAGE || inputType === InputTypeEnum.VIDEO) {
    return (
      <WorkDataMedia
        item={{
          text: item.name,
          files: (item.item as IWorkContentDocumentsItem)?.documents,
          formName: `${prefix}${item.id}`,
          mandatory: item.mandatory,
          maxFilesAllowed: item.maxFilesAllowed,
        }}
        isEditable={isEditable}
        mediaType={inputType}
        key={item.id}
      />
    );
  }

  if (inputType === InputTypeEnum.AUDIO) {
    return (
      <WorkDataAudio
        item={{
          text: item.name,
          files: (item.item as IWorkContentDocumentsItem)?.documents,
          formName: `${prefix}${item.id}`,
          mandatory: item.mandatory,
          maxFilesAllowed: item.maxFilesAllowed,
        }}
        isEditable={isEditable}
        key={item.id}
      />
    );
  }

  if (inputType === InputTypeEnum.MULTIPLE) {
    return (
      <WorkDataMultiple
        item={{
          text: item.name,
          files: (item.item as IWorkContentDocumentsItem)?.documents,
          formName: `${prefix}${item.id}`,
          mandatory: item.mandatory,
          maxFilesAllowed: item.maxFilesAllowed,
        }}
        isEditable={isEditable}
        key={item.id}
      />
    );
  }

  return null;
};

export const WorkDataDocumentsForm: FC<IWorkDataDocuments> = ({item, isEditable}) => {
  return (
    <Stack style={{flex: 1, marginBottom: '10px'}}>
      <Stack flexDirection="column" spacing={1}>
        <PhotoProvider
          overlayRender={({images, index}) => {
            return (
              <Stack
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  width: '100%',
                  padding: '.5rem',
                  zIndex: 50,
                  background: '#00000080',
                }}
              >
                <Stack>{images[index].overlay}</Stack>
              </Stack>
            );
          }}
          photoClassName={styles.photoViewContainer}
          toolbarRender={(props) => (
            <ToolBar
              photoNames={item.fields.reduce<string[]>((acc, curr) => {
                if (curr.item && curr.type === WorkContentType.DOCUMENTS) {
                  return [
                    ...acc,
                    ...curr.item.documents.map((i, ind) => {
                      const splittedFileName = i.fileName.split('.');
                      const fileExt = splittedFileName[splittedFileName.length - 1];

                      return `${curr.name}_${ind + 1}.${fileExt}`;
                    }),
                  ];
                }

                return acc;
              }, [])}
              {...props}
            />
          )}
        >
          {item.fields.map((i) => (
            <RenderDocuments key={i.id} item={i} isEditable={isEditable} />
          ))}
        </PhotoProvider>
      </Stack>
    </Stack>
  );
};
